import { useState, useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { withPrefix } from 'gatsby';
import { User } from 'oidc-client';
import { AppState } from '../store';
import userManager from '../util/usermanager';
import { SubscriptionLoadingStatus } from '../store/subscriptions/types';
import { loadUserSubscriptionData } from '../store/subscriptions/actions';
import { loadAnnouncements } from '../store/announcement/actions';
import { finishRedirectSafe } from '../util/usermanager';

const pathPrefix = withPrefix('/');
const r = new RegExp(`^(${pathPrefix})`);

const generateReturnPath = (l: Location): string => {
  const normalizedPath = l?.pathname ?? '/';
  const relativeReturn = normalizedPath.startsWith('/')
    ? normalizedPath
    : '/' + normalizedPath;

  const returnPath = relativeReturn.replace(r, '');
  if (returnPath.startsWith('/')) {
    return returnPath;
  }

  return '/' + returnPath;
};

const useProtectPage = (
  u: User | undefined
): [boolean, (user: User | undefined) => void] => {
  const [user, setUser] = useState<User | undefined>(u);
  const [loggedIn, setLoggedIn] = useState(!(user?.expired ?? true));
  const [loginRequested, setLoginRequested] = useState(false);

  const dispatch = useDispatch();

  const userSubDetailsLoaded = useSelector(
    (state: AppState) => state.subscriptions.userDataLoaded
  );
  const subscriptionLoadingState = useSelector(
    (state: AppState) => state.subscriptions.loadState
  );

  const needsAnnouncements = useSelector(
    (state: AppState) =>
      !state.announcement.loaded && !state.announcement.loading
  );

  useEffect(() => {
    if (loggedIn) {
      finishRedirectSafe();
      const token = user?.access_token ?? '';
      if (token !== '') {
        if (
          !userSubDetailsLoaded &&
          subscriptionLoadingState !== SubscriptionLoadingStatus.Loading
        ) {
          dispatch(loadUserSubscriptionData(token));
        }
        if (needsAnnouncements) {
          dispatch(loadAnnouncements(token));
        }
      }
    }
  }, [
    loggedIn,
    needsAnnouncements,
    userSubDetailsLoaded,
    subscriptionLoadingState,
  ]);

  let redirectStarted = false;

  useEffect(() => {
    const isSSR =
      typeof window === 'undefined' || typeof userManager === 'undefined';
    if (!isSSR) {
      const currentLoginStatus = !(user?.expired ?? true);
      if (!currentLoginStatus && !loginRequested) {
        console.log('redirecting to login...');

        const returnPath = generateReturnPath(location);
        if (!redirectStarted) {
          redirectStarted = true;
          userManager?.signinRedirect({ state: { pathname: returnPath } });
        }
        setLoginRequested(true);
      }
      setLoggedIn(currentLoginStatus);
    }
  }, [user]);

  return [loggedIn, (u) => setUser(u)];
};

export { useProtectPage };
